import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useCleoStore } from '@/stores/cleoChat';
import { KPN } from '@/types/permissions/KPN';
import { InterfaceFilterMultipleSeparator } from '@/types/object_classes/InterfaceListingFilter';
import type { NavigationAction, NavigationOffer } from '@/types/ai/cleoChat';
import type { LocationQueryValue } from 'vue-router';

interface FilterMapEntry {
    urlKey: string;
    implies?: Record<string, string>;
}

// `label` here is a fallback used only when the LLM did not provide one (legacy
// callsites or future tooling). For Cleo navigation offers the label always
// comes from the offer itself — see mapOfferToAction.
interface NavConfig {
    routeName: string;
    requiredKPN: string[];
    label: string;
    filterMapping: Record<string, FilterMapEntry>;
    paramMapping?: Record<string, string>;
    staticParams?: Record<string, string>;
}

const NAVIGATION_MAP: Record<string, NavConfig> = {
    'company-detail': {
        routeName: 'company',
        requiredKPN: [KPN.MANAGECOMPANY_ENABLED],
        label: 'View Company',
        paramMapping: { id: 'id' },
        filterMapping: {},
    },
    'contact-detail': {
        routeName: 'contact',
        requiredKPN: [KPN.CONTACTDETAIL_ENABLED],
        label: 'View Contact',
        paramMapping: { id: 'id' },
        filterMapping: {},
    },
    'domestic-shipment-detail': {
        routeName: 'shipment-overview',
        requiredKPN: [KPN.DOMESTICSHIPMENTOVERVIEW_ENABLED],
        label: 'View Shipment',
        paramMapping: { id: 'shipment_id' },
        staticParams: { transport_type: 'domestic' },
        filterMapping: {},
    },
    'international-shipment-detail': {
        routeName: 'shipment-overview',
        requiredKPN: [KPN.INTERNATIONALSHIPMENTOVERVIEW_ENABLED],
        label: 'View Shipment',
        paramMapping: { id: 'shipment_id' },
        staticParams: { transport_type: 'international' },
        filterMapping: {},
    },
    'strongo-shipment-detail': {
        routeName: 'strongo-shipment-overview',
        requiredKPN: [KPN.STRONGOSHIPMENTOVERVIEW_ENABLED],
        label: 'View Strongo Shipment',
        paramMapping: { id: 'shipment_id' },
        filterMapping: {},
    },
    'sea-shipment': {
        routeName: 'ocean',
        requiredKPN: [KPN.OCEAN_ENABLED, KPN.BUSINESSLINESORDERS_ENABLED],
        label: 'View in Sea Shipments',
        filterMapping: {
            transport_shipment_code:    { urlKey: 'search', implies: { search_select: 'transport_shipment_code' } },
            shipment_code:              { urlKey: 'search', implies: { search_select: 'transport_shipment_code' } },
            customer_reference:         { urlKey: 'search', implies: { search_select: 'customer_reference' } },
            house_bill_no:              { urlKey: 'search', implies: { search_select: 'house_bill_no' } },
            shipper_name:               { urlKey: 'search', implies: { search_select: 'shipper_name' } },
            delivery:                   { urlKey: 'search', implies: { search_select: 'delivery' } },
            containers:                 { urlKey: 'search', implies: { search_select: 'containers' } },
            consol_code:                { urlKey: 'search', implies: { search_select: 'consol_code' } },
            invoice_number:             { urlKey: 'search', implies: { search_select: 'invoice_number' } },
            shipment_status:            { urlKey: 'shipment_status' },
            billing_external_reference_code: { urlKey: 'client' },
            from_country_name:          { urlKey: 'from_country_name' },
            to_country_name:            { urlKey: 'to_country_name' },
            vessel_name:                { urlKey: 'vessel_name' },
            voyage_number:              { urlKey: 'voyage_number' },
            origin_point:               { urlKey: 'origin_point' },
            destination_point:          { urlKey: 'destination_point' },
            secondary_type:             { urlKey: 'secondary_type' },
            type:                       { urlKey: 'type' },
            pack_type:                  { urlKey: 'pack_type' },
            has_invoice:                { urlKey: 'has_invoice' },
            has_contract:               { urlKey: 'has_contract' },
            creation_date:              { urlKey: 'creation_date' },
            etd:                        { urlKey: 'etd' },
            atd:                        { urlKey: 'atd' },
            eta:                        { urlKey: 'eta' },
            add:                        { urlKey: 'add' },
            delivered_on:               { urlKey: 'ata' },
            destination_delivery_edd:   { urlKey: 'edd' },
        },
    },
    'air-shipment': {
        routeName: 'air',
        requiredKPN: [KPN.AIR_ENABLED, KPN.BUSINESSLINESORDERS_ENABLED],
        label: 'View in Air Shipments',
        filterMapping: {
            transport_shipment_code:    { urlKey: 'search', implies: { search_select: 'transport_shipment_code' } },
            shipment_code:              { urlKey: 'search', implies: { search_select: 'transport_shipment_code' } },
            mawb:                       { urlKey: 'search', implies: { search_select: 'mawb' } },
            customer_reference:         { urlKey: 'search', implies: { search_select: 'customer_reference' } },
            delivery:                   { urlKey: 'search', implies: { search_select: 'delivery' } },
            consol_code:                { urlKey: 'search', implies: { search_select: 'consol_code' } },
            invoice_number:             { urlKey: 'search', implies: { search_select: 'invoice_number' } },
            shipment_status:            { urlKey: 'shipment_status' },
            billing_external_reference_code: { urlKey: 'client' },
            from_country_name:          { urlKey: 'from_country_name' },
            to_country_name:            { urlKey: 'to_country_name' },
            origin_point:               { urlKey: 'origin_point' },
            destination_point:          { urlKey: 'destination_point' },
            flight:                     { urlKey: 'flight' },
            secondary_type:             { urlKey: 'secondary_type' },
            pack_type:                  { urlKey: 'pack_type' },
            has_invoice:                { urlKey: 'has_invoice' },
            has_contract:               { urlKey: 'has_contract' },
            creation_date:              { urlKey: 'creation_date' },
            etd:                        { urlKey: 'etd' },
            atd:                        { urlKey: 'atd' },
            eta:                        { urlKey: 'eta' },
            add:                        { urlKey: 'add' },
            delivered_on:               { urlKey: 'to_ata' },
            destination_delivery_edd:   { urlKey: 'edd' },
        },
    },
    'domestic-shipment': {
        routeName: 'domestic-shipments',
        requiredKPN: [KPN.DOMESTICROAD_ENABLED, KPN.BUSINESSLINESORDERS_ENABLED],
        label: 'View in Domestic Shipments',
        filterMapping: {
            perm_shipment_id:           { urlKey: 'search', implies: { search_select: 'perm_shipment_id' } },
            temp_shipment_id:           { urlKey: 'search', implies: { search_select: 'temp_shipment_id' } },
            reference:                  { urlKey: 'search', implies: { search_select: 'reference' } },
            financial_reference_1:      { urlKey: 'search', implies: { search_select: 'financial_reference_1' } },
            uit_code:                   { urlKey: 'search', implies: { search_select: 'uit_code' } },
            shipment_status:            { urlKey: 'shipment_status' },
            consignor_company_code:     { urlKey: 'client' },
            pickup_address:             { urlKey: 'pickup_address' },
            delivery_address:           { urlKey: 'delivery_address' },
            loading_city_name:          { urlKey: 'loading_city_name' },
            unloading_city_name:        { urlKey: 'unloading_city_name' },
            loading_county_code:        { urlKey: 'loading_county_code' },
            unloading_county_code:      { urlKey: 'unloading_county_code' },
            package_code:               { urlKey: 'package_code' },
            department:                 { urlKey: 'department' },
            key_account_manager:        { urlKey: 'key_account_manager' },
            unload_delay_kpi:           { urlKey: 'unload_delay_kpi' },
            has_invoice:                { urlKey: 'has_invoice' },
            has_uit:                    { urlKey: 'has_uit' },
            has_contract:               { urlKey: 'has_contract' },
            has_pod:                    { urlKey: 'has_pod' },
            source:                     { urlKey: 'source' },
            shipment_date:              { urlKey: 'shipment_date' },
            loading_start_date:         { urlKey: 'loading_start_date' },
            unloading_start_date:       { urlKey: 'unloading_start_date' },
            loading_act_start_date:     { urlKey: 'loading_act_start_date' },
            unloading_act_start_date:   { urlKey: 'unloading_act_start_date' },
        },
    },
    'international-shipment': {
        routeName: 'international-shipments',
        requiredKPN: [KPN.INTERNATIONALROAD_ENABLED, KPN.BUSINESSLINESORDERS_ENABLED],
        label: 'View in International Shipments',
        filterMapping: {
            perm_shipment_id:           { urlKey: 'search', implies: { search_select: 'perm_shipment_id' } },
            temp_shipment_id:           { urlKey: 'search', implies: { search_select: 'temp_shipment_id' } },
            reference:                  { urlKey: 'search', implies: { search_select: 'reference' } },
            financial_reference_1:      { urlKey: 'search', implies: { search_select: 'financial_reference_1' } },
            uit_code:                   { urlKey: 'search', implies: { search_select: 'uit_code' } },
            shipment_status:            { urlKey: 'shipment_status' },
            consignor_company_code:     { urlKey: 'client' },
            import_export:              { urlKey: 'import_export' },
            loading_country_code:       { urlKey: 'loading_country_code' },
            unloading_country_code:     { urlKey: 'unloading_country_code' },
            loading_city_name:          { urlKey: 'loading_city_name' },
            unloading_city_name:        { urlKey: 'unloading_city_name' },
            loading_county_code:        { urlKey: 'loading_county_code' },
            unloading_county_code:      { urlKey: 'unloading_county_code' },
            package_code:               { urlKey: 'package_code' },
            department:                 { urlKey: 'department' },
            key_account_manager:        { urlKey: 'key_account_manager' },
            unload_delay_kpi:           { urlKey: 'unload_delay_kpi' },
            has_invoice:                { urlKey: 'has_invoice' },
            has_uit:                    { urlKey: 'has_uit' },
            has_contract:               { urlKey: 'has_contract' },
            has_pod:                    { urlKey: 'has_pod' },
            source:                     { urlKey: 'source' },
            shipment_date:              { urlKey: 'shipment_date' },
            loading_start_date:         { urlKey: 'loading_start_date' },
            unloading_start_date:       { urlKey: 'unloading_start_date' },
            loading_act_start_date:     { urlKey: 'loading_act_start_date' },
            unloading_act_start_date:   { urlKey: 'unloading_act_start_date' },
        },
    },
    'strongo-shipment': {
        routeName: 'strongo-shipments',
        requiredKPN: [KPN.STRONGO_ENABLED, KPN.BUSINESSLINESORDERS_ENABLED],
        label: 'View in Strongo Shipments',
        filterMapping: {
            no:                         { urlKey: 'search', implies: { search_select: 'no' } },
            customer_reference:         { urlKey: 'search', implies: { search_select: 'customer_reference' } },
            invoice_number:             { urlKey: 'search', implies: { search_select: 'invoice_number' } },
            client_code:                { urlKey: 'client' },
            status:                     { urlKey: 'status' },
            from_city:                  { urlKey: 'from_city' },
            from_county:                { urlKey: 'from_county' },
            from_address:               { urlKey: 'from_address' },
            to_city:                    { urlKey: 'to_city' },
            to_county:                  { urlKey: 'to_county' },
            to_address:                 { urlKey: 'to_address' },
            key_account_manager:        { urlKey: 'key_account_manager' },
            has_invoice:                { urlKey: 'has_invoice' },
            has_contract:               { urlKey: 'has_contract' },
            from_time:                  { urlKey: 'from_time' },
            to_time:                    { urlKey: 'to_time' },
            scheduled_pickup_date:      { urlKey: 'scheduled_pickup_date' },
            pickup_date:                { urlKey: 'pickup_date' },
            last_status_date:           { urlKey: 'last_status_date' },
        },
    },
    'simplify-inbound': {
        routeName: 'inbounds',
        requiredKPN: [KPN.WAREHOUSINGSIMPLIFYRECEPTIONRETURN_ENABLED, KPN.BUSINESSLINESORDERS_ENABLED],
        label: 'View in Inbounds',
        filterMapping: {
            cod_client:                  { urlKey: 'client' },
            status:                      { urlKey: 'status' },
            has_contract:                { urlKey: 'has_contract' },
            creation_date:               { urlKey: 'creation_date' },
            data_preconizata_receptie:   { urlKey: 'data_preconizata_receptie' },
        },
    },
    'simplify-outbound': {
        routeName: 'outbounds',
        requiredKPN: [KPN.WAREHOUSINGSIMPLIFYORDER_ENABLED, KPN.BUSINESSLINESORDERS_ENABLED],
        label: 'View in Outbounds',
        filterMapping: {
            cod_client_3pl:              { urlKey: 'client' },
            status:                      { urlKey: 'status' },
            has_contract:                { urlKey: 'has_contract' },
            tara_livrare:                { urlKey: 'tara_livrare' },
            shipper:                     { urlKey: 'shipper' },
            creation_date:               { urlKey: 'creation_date' },
            data_livrare:                { urlKey: 'data_livrare' },
        },
    },
    'vbs-inbound': {
        routeName: 'vbs-inbounds',
        requiredKPN: [KPN.WAREHOUSINGRECEPTIONRETURN_ENABLED, KPN.BUSINESSLINESORDERS_ENABLED],
        label: 'View in VBS Inbounds',
        filterMapping: {
            client_no:                   { urlKey: 'client' },
            reception_status:            { urlKey: 'reception_status' },
            has_contract:                { urlKey: 'has_contract' },
            oneclick_upload:             { urlKey: 'oneclick_upload' },
            creation_date:               { urlKey: 'creation_date' },
            delivery_date:               { urlKey: 'delivery_date' },
        },
    },
    'vbs-outbound': {
        routeName: 'vbs-outbounds',
        requiredKPN: [KPN.WAREHOUSINGORDERS_ENABLED, KPN.BUSINESSLINESORDERS_ENABLED],
        label: 'View in VBS Outbounds',
        filterMapping: {
            client_no:                   { urlKey: 'client' },
            order_status:                { urlKey: 'order_status' },
            has_contract:                { urlKey: 'has_contract' },
            oneclick_upload:             { urlKey: 'oneclick_upload' },
            county:                      { urlKey: 'county' },
            ship:                        { urlKey: 'ship' },
            creation_date:               { urlKey: 'creation_date' },
            delivery_date:               { urlKey: 'delivery_date' },
        },
    },
    'customer-invoice': {
        routeName: 'customers',
        requiredKPN: [KPN.INVOICES_ENABLED, KPN.ISSUEDBYKLG_ENABLED],
        label: 'View in Invoices',
        filterMapping: {
            customer_no:                 { urlKey: 'customer_code' },
            document_no:                 { urlKey: 'document_no' },
            currency_code:               { urlKey: 'currency_code' },
            document_type:               { urlKey: 'document_type' },
            remaining_amount:            { urlKey: 'remaining_amount' },
            remaining_amt_lcy:           { urlKey: 'remaining_amt_lcy' },
            overdue:                     { urlKey: 'overdue' },
            invoice:                     { urlKey: 'invoice' },
            invoice_details:             { urlKey: 'invoice_details' },
            service_type:                { urlKey: 'service_type' },
            key_account_manager:         { urlKey: 'key_account_manager' },
            contract_manager:            { urlKey: 'contract_manager' },
            sales_persons_code:          { urlKey: 'sales_persons_code' },
            indoor_sales_person:         { urlKey: 'indoor_sales_person' },
            document_date:               { urlKey: 'document_date' },
            due_date:                    { urlKey: 'due_date' },
            customer_name:               { urlKey: 'customer_code' },
        },
    },
    'customer-invoice-detail': {
        routeName: 'customer-invoice-details',
        requiredKPN: [KPN.INVOICES_ENABLED, KPN.ISSUEDBYKLG_ENABLED, KPN.CUSTOMERINVOICEDETAILS_ENABLED],
        label: 'View Invoice',
        paramMapping: { entry_no: 'entry_no' },
        filterMapping: {},
    },
    'company-listing': {
        routeName: 'clients-listing',
        requiredKPN: [KPN.COMPANIESANDPERMISSIONS_ENABLED, KPN.COMPANIES_ENABLED],
        label: 'View in Companies',
        filterMapping: {
            name:                     { urlKey: 'name' },
            tms_road_unique_code:     { urlKey: 'search', implies: { search_select: 'tms_road_unique_code' } },
            key_account_manager_id:   { urlKey: 'key_account_manager_id' },
            sales_persons_code_id:    { urlKey: 'sales_persons_code_id' },
            contract_manager:         { urlKey: 'contract_manager' },
            indoor_sales_person:      { urlKey: 'indoor_sales_person' },
            city:                     { urlKey: 'city' },
            county:                   { urlKey: 'county' },
            country:                  { urlKey: 'country' },
            status:                   { urlKey: 'status' },
            business_line:            { urlKey: 'business_line' },
            erp_blocked_status:       { urlKey: 'erp_blocked_status' },
            marketing_ratio_id:       { urlKey: 'marketing_ratio_id' },
            industry_id:              { urlKey: 'industry_id' },
            business_category_id:     { urlKey: 'business_category_id' },
            company_type_id:          { urlKey: 'company_type_id' },
            parent_account_id:        { urlKey: 'parent_account_id' },
            contract_clauses:         { urlKey: 'contract_clauses' },
            has_order_entry:          { urlKey: 'has_order_entry' },
            has_errors:               { urlKey: 'has_errors' },
            self_service_api_user:    { urlKey: 'self_service_api_user' },
            self_service_api:         { urlKey: 'self_service_api' },
            has_transport_contract:   { urlKey: 'has_transport_contract' },
            has_warehousing_contract: { urlKey: 'has_warehousing_contract' },
            first_order_date:         { urlKey: 'first_order_date' },
            last_invoice_date:        { urlKey: 'last_invoice_date' },
            sales_approved_at:        { urlKey: 'sales_approved_at' },
            financial_approved_at:    { urlKey: 'financial_approved_at' },
            contract_date:            { urlKey: 'contract_date' },
        },
    },
    'contacts': {
        routeName: 'contacts',
        requiredKPN: [KPN.COMPANIESANDPERMISSIONS_ENABLED, KPN.CONTACTS_ENABLED],
        label: 'View in Contacts',
        filterMapping: {
            first_name:  { urlKey: 'first_name' },
            last_name:   { urlKey: 'last_name' },
            company_id:  { urlKey: 'company' },
            city:        { urlKey: 'city' },
            county:      { urlKey: 'county' },
            country:     { urlKey: 'country' },
            gender_id:   { urlKey: 'gender_id' },
            language_id: { urlKey: 'language_id' },
            status:      { urlKey: 'status' },
        },
    },
    'klg-users': {
        routeName: 'klg-users-listing',
        requiredKPN: [KPN.USERS_ENABLED],
        label: 'View in KLG Users',
        filterMapping: {
            first_name:                { urlKey: 'first_name' },
            last_name:                 { urlKey: 'last_name' },
            email:                     { urlKey: 'email' },
            job_title:                 { urlKey: 'job_title' },
            department:                { urlKey: 'department' },
            department_id:             { urlKey: 'department_id' },
            sales_person_purchaser_id: { urlKey: 'sales_person_purchaser_id' },
            tms_owner_id:              { urlKey: 'tms_owner_id' },
            key_account_id:            { urlKey: 'key_account_id' },
            status:                    { urlKey: 'status' },
            role:                      { urlKey: 'role' },
            last_access:               { urlKey: 'last_access' },
            uses_mcp:                  { urlKey: 'uses_mcp' },
        },
    },
    'klg-user-detail': {
        routeName: 'klg-manage-user',
        requiredKPN: [KPN.KLGUSERSANDPERMISSIONS_ENABLED, KPN.MANAGEUSER_ENABLED],
        label: 'View User',
        paramMapping: { userId: 'id', id: 'id' },
        filterMapping: {},
    },
    'company-users': {
        routeName: 'company-users',
        requiredKPN: [KPN.COMPANIESANDPERMISSIONS_ENABLED, KPN.COMPANYUSERS_ENABLED],
        label: 'View in Company Users',
        filterMapping: {
            first_name:        { urlKey: 'first_name' },
            last_name:         { urlKey: 'last_name' },
            email:             { urlKey: 'email' },
            role:              { urlKey: 'role' },
            marketing_ratio:   { urlKey: 'marketing_ratio' },
            company_id:        { urlKey: 'client_name' },
            last_access:       { urlKey: 'last_access' },
            uses_mcp:          { urlKey: 'uses_mcp' },
        },
    },
    'client-roles-permissions': {
        routeName: 'client-roles-permissions',
        requiredKPN: [KPN.COMPANIESANDPERMISSIONS_ENABLED, KPN.ROLESANDPERMISSION_ENABLED],
        label: 'View Client Roles & Permissions',
        filterMapping: {},
    },
    'indoor-sales': {
        routeName: 'indoor-sales',
        requiredKPN: [KPN.SITECONFIGURATION_ENABLED, KPN.INDOORSALES_ENABLED],
        label: 'View Indoor Sales',
        filterMapping: {},
    },
    'key-account-managers': {
        routeName: 'key-account-managers',
        requiredKPN: [KPN.SITECONFIGURATION_ENABLED, KPN.KEYACCOUNTMANAGERS_ENABLED],
        label: 'View Key Account Managers',
        filterMapping: {},
    },
    'sales-persons': {
        routeName: 'sales-persons',
        requiredKPN: [KPN.SITECONFIGURATION_ENABLED, KPN.SALESPERSONS_ENABLED],
        label: 'View Sales Persons',
        filterMapping: {},
    },
    'releases': {
        routeName: 'releases',
        requiredKPN: [KPN.ANNOUNCEMENTS_ENABLED, KPN.RELEASES_ENABLED],
        label: 'View Releases',
        filterMapping: {},
    },
    'maintenances': {
        routeName: 'maintenances',
        requiredKPN: [KPN.SITEADMIN_ENABLED, KPN.MAINTENANCES_ENABLED],
        label: 'View Maintenances',
        filterMapping: {},
    },
    'site-notifications': {
        routeName: 'site-notifications',
        requiredKPN: [KPN.ANNOUNCEMENTS_ENABLED, KPN.SITENOTIFICATIONS_ENABLED],
        label: 'View Site Notifications',
        filterMapping: {},
    },
    'account-receivables': {
        routeName: 'account-receivables',
        requiredKPN: [KPN.COMPANIESANDPERMISSIONS_ENABLED, KPN.ACCOUNTRECEIVABLES_ENABLED],
        label: 'View Account Receivables',
        filterMapping: {
            client_name:                { urlKey: 'client_name' },
            client_code:                { urlKey: 'client_code' },
            vat_number:                 { urlKey: 'vat_number' },
            country:                    { urlKey: 'country' },
            key_account_manager:        { urlKey: 'key_account_manager' },
            service_type:               { urlKey: 'service_type' },
            payment_term_display_name:  { urlKey: 'payment_term_display_name' },
            has_contract:               { urlKey: 'has_contract' },
            has_clause:                 { urlKey: 'has_clause' },
            first_order_date:           { urlKey: 'first_order_date' },
            last_invoice_date:          { urlKey: 'last_invoice_date' },
            marketing_ratio:            { urlKey: 'marketing_ratio' },
            sales_ratio:                { urlKey: 'sales_ratio' },
            industry:                   { urlKey: 'industry' },
            business_category:          { urlKey: 'business_category' },
            show_overdue_balance:       { urlKey: 'show_overdue_balance' },
            klg_color:                  { urlKey: 'klg_color' },
        },
    },
    'currencies': {
        routeName: 'currencies',
        requiredKPN: [KPN.SITECONFIGURATION_ENABLED, KPN.CURRENCIES_ENABLED],
        label: 'View Currencies',
        filterMapping: {},
    },
    'legal-holidays': {
        routeName: 'legal-holidays',
        requiredKPN: [KPN.TRANSPORTSITECONFIGURATION_ENABLED, KPN.LEGALHOLIDAYS_ENABLED],
        label: 'View Legal Holidays',
        filterMapping: {},
    },
    'activity-types': {
        routeName: 'activity-types',
        requiredKPN: [KPN.TRANSPORTSITECONFIGURATION_ENABLED, KPN.ACTIVITYTYPES_ENABLED],
        label: 'View Activity Types',
        filterMapping: {},
    },
    'activity-statuses': {
        routeName: 'activity-statuses',
        requiredKPN: [KPN.TRANSPORTSITECONFIGURATION_ENABLED, KPN.ACTIVITYSTATUSES_ENABLED],
        label: 'View Activity Statuses',
        filterMapping: {},
    },
    'departments': {
        routeName: 'departments',
        requiredKPN: [KPN.SITECONFIGURATION_ENABLED, KPN.DEPARTMENTS_ENABLED],
        label: 'View Departments',
        filterMapping: {},
    },
    'shippers': {
        routeName: 'shippers',
        requiredKPN: [KPN.WAREHOUSINGSITECONFIGURATION_ENABLED, KPN.SHIPPERS_ENABLED],
        label: 'View Shippers',
        filterMapping: {},
    },
    'api-sources': {
        routeName: 'api-sources',
        requiredKPN: [KPN.SITECONFIGURATION_ENABLED, KPN.APISOURCES_ENABLED],
        label: 'View API Sources',
        filterMapping: {},
    },
    'api-statuses': {
        routeName: 'api-statuses',
        requiredKPN: [KPN.SITECONFIGURATION_ENABLED, KPN.APISTATUSES_ENABLED],
        label: 'View API Statuses',
        filterMapping: {},
    },
    'app-statuses': {
        routeName: 'app-statuses',
        requiredKPN: [KPN.SITECONFIGURATION_ENABLED, KPN.APPSTATUSES_ENABLED],
        label: 'View App Statuses',
        filterMapping: {},
    },
    'klg-roles-permissions': {
        routeName: 'klg-roles-permissions',
        requiredKPN: [KPN.KLGUSERSANDPERMISSIONS_ENABLED, KPN.ROLESANDPERMISSIONS_ENABLED],
        label: 'View KLG Roles & Permissions',
        filterMapping: {},
    },
    'allowed-register-domains': {
        routeName: 'allowed-register-domains',
        requiredKPN: [KPN.SITECONFIGURATION_ENABLED, KPN.ALLOWEDREGISTERDOMAIN_ENABLED],
        label: 'View Allowed Register Domains',
        filterMapping: {},
    },
    'authorized-emails': {
        routeName: 'authorized-emails',
        requiredKPN: [KPN.SITECONFIGURATION_ENABLED, KPN.AUTHORIZEDEMAILS_ENABLED],
        label: 'View Authorized Emails',
        filterMapping: {},
    },
    'webmail-domains': {
        routeName: 'webmail-domains',
        requiredKPN: [KPN.SITECONFIGURATION_ENABLED, KPN.WEBMAILDOMAINS_ENABLED],
        label: 'View Webmail Domains',
        filterMapping: {},
    },
    'option-categories-listing': {
        routeName: 'option-categories-listing',
        requiredKPN: [KPN.SITEADMIN_ENABLED, KPN.OPTIONCATEGORIES_ENABLED],
        label: 'View Option Categories',
        filterMapping: {},
    },
    'option-category-values-listing': {
        routeName: 'option-category-values-listing',
        requiredKPN: [KPN.SITEADMIN_ENABLED, KPN.OPTIONVALUES_ENABLED],
        label: 'View Option Values',
        paramMapping: { option_category_id: 'option_category_id', category_id: 'option_category_id' },
        filterMapping: {},
    },
    'tooltip-categories': {
        routeName: 'tooltip-categories',
        requiredKPN: [KPN.SITECONFIGURATION_ENABLED, KPN.TOOLTIPCATEGORIES_ENABLED],
        label: 'View Tooltip Categories',
        filterMapping: {},
    },
    'tooltip-values': {
        routeName: 'tooltip-values',
        requiredKPN: [KPN.SITECONFIGURATION_ENABLED, KPN.TOOLTIPVALUES_ENABLED],
        label: 'View Tooltip Values',
        paramMapping: { tooltip_category_id: 'tooltip_category_id' },
        filterMapping: {},
    },
    'general-settings': {
        routeName: 'general-settings',
        requiredKPN: [KPN.ORDERENTRYSETTINGSMODULE_ENABLED, KPN.ORDERENTRYGENERALSETTINGS_ENABLED],
        label: 'View General Settings',
        filterMapping: {},
    },
    'incoterms': {
        routeName: 'incoterms',
        requiredKPN: [KPN.ORDERENTRYSETTINGSMODULE_ENABLED, KPN.ORDERENTRYINCOTERMS_ENABLED],
        label: 'View Incoterms',
        filterMapping: {},
    },
    'packages': {
        routeName: 'packages',
        requiredKPN: [KPN.ORDERENTRYSETTINGSMODULE_ENABLED, KPN.ORDERENTRYPACKAGES_ENABLED],
        label: 'View Packages',
        filterMapping: {},
    },
    'order-entry-features': {
        routeName: 'features',
        requiredKPN: [KPN.ORDERENTRYSETTINGSMODULE_ENABLED, KPN.ORDERENTRYFEATURES_ENABLED],
        label: 'View Order Entry Features',
        filterMapping: {},
    },
};

interface ResourceMapping {
    listing: string;
    detail?: string;
    detailKey?: string;
}

const JSON_API_TO_RESOURCE_TYPE: Record<string, ResourceMapping> = {
    'companies':                       { listing: 'company-listing',          detail: 'company-detail',                detailKey: 'id' },
    'contacts':                        { listing: 'contacts',                 detail: 'contact-detail',                detailKey: 'id' },
    'domestic-shipments':              { listing: 'domestic-shipment',        detail: 'domestic-shipment-detail',      detailKey: 'id' },
    'international-shipments':         { listing: 'international-shipment',   detail: 'international-shipment-detail', detailKey: 'id' },
    'tms-sea-shipments':               { listing: 'sea-shipment' },
    'tms-air-shipments':               { listing: 'air-shipment' },
    'strongo-shipments':               { listing: 'strongo-shipment',         detail: 'strongo-shipment-detail',       detailKey: 'id' },
    'inbounds':                        { listing: 'simplify-inbound' },
    'outbounds':                       { listing: 'simplify-outbound' },
    'vbs-report-inbounds':             { listing: 'vbs-inbound' },
    'vbs-report-outbounds':            { listing: 'vbs-outbound' },
    'customer-ledger-entries':         { listing: 'customer-invoice',         detail: 'customer-invoice-detail',       detailKey: 'entry_no' },
    'users':                           { listing: 'klg-users',                detail: 'klg-user-detail',               detailKey: 'id' },
    'client-users':                    { listing: 'company-users' },
    'client-roles':                    { listing: 'client-roles-permissions' },
    'client-permissions':              { listing: 'client-roles-permissions' },
    'indoor-sales':                    { listing: 'indoor-sales' },
    'key-account-managers':            { listing: 'key-account-managers' },
    'sales-persons':                   { listing: 'sales-persons' },
    'releases':                        { listing: 'releases' },
    'maintenances':                    { listing: 'maintenances' },
    'site-notifications':              { listing: 'site-notifications' },
    'account-receivables':             { listing: 'account-receivables' },
    'activity-types':                  { listing: 'activity-types' },
    'activity-statuses':               { listing: 'activity-statuses' },
    'currencies':                      { listing: 'currencies' },
    'legal-holidays':                  { listing: 'legal-holidays' },
    'departments':                     { listing: 'departments' },
    'shippers':                        { listing: 'shippers' },
    'api-sources':                     { listing: 'api-sources' },
    'api-statuses':                    { listing: 'api-statuses' },
    'app-statuses':                    { listing: 'app-statuses' },
    'roles':                           { listing: 'klg-roles-permissions' },
    'klg-roles':                       { listing: 'klg-roles-permissions' },
    'klg-permissions':                 { listing: 'klg-roles-permissions' },
    'allowed-register-domains':        { listing: 'allowed-register-domains' },
    'authorized-emails':               { listing: 'authorized-emails' },
    'webmail-domains':                 { listing: 'webmail-domains' },
    'option-categories':               { listing: 'option-categories-listing' },
    'option-values':                   { listing: 'option-category-values-listing' },
    'tooltip-categories':              { listing: 'tooltip-categories' },
    'tooltip-values':                  { listing: 'tooltip-values' },
    'incoterms':                       { listing: 'incoterms' },
    'packages':                        { listing: 'packages' },
    'order-entry-features':            { listing: 'order-entry-features' },
    'domestic-order-entry-general-settings':      { listing: 'general-settings' },
    'international-order-entry-general-settings': { listing: 'general-settings' },
    'strongo-order-entry-general-settings':       { listing: 'general-settings' },
    'general-settings':                { listing: 'general-settings' },
};

type QueryValue = LocationQueryValue | LocationQueryValue[];

const DATE_RANGE_LONG_RE  = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})\s*,\s*(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})$/;
const DATE_RANGE_SHORT_RE = /^(\d{4}-\d{2}-\d{2})\s*,\s*(\d{4}-\d{2}-\d{2})$/;

// Accepts "YYYY-MM-DD,YYYY-MM-DD" (full-day, defaults to 00:00:00 / 23:59:59 local)
// or legacy "YYYY-MM-DD HH:MM:SS, YYYY-MM-DD HH:MM:SS".
const parseDateRangeForUrl = (value: string): [string, string] | null => {
    const longMatch = DATE_RANGE_LONG_RE.exec(value);
    if (longMatch) {
        const [, startDate, startTime, endDate, endTime] = longMatch;
        const startISO = new Date(`${startDate}T${startTime}`).toISOString();
        const endISO   = new Date(new Date(`${endDate}T${endTime}`).getTime() + 999).toISOString();
        return [startISO, endISO];
    }

    const shortMatch = DATE_RANGE_SHORT_RE.exec(value);
    if (shortMatch) {
        const [, startDate, endDate] = shortMatch;
        const [sy, sm, sd] = startDate.split('-').map(Number);
        const [ey, em, ed] = endDate.split('-').map(Number);
        const start = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
        const end   = new Date(ey, em - 1, ed, 23, 59, 59, 999);
        return [start.toISOString(), end.toISOString()];
    }

    return null;
};

// `has_*` boolean filters in this codebase are consistently 'yes' / 'no' strings
// (ShipmentHasInvoiceFilter, ShipmentHasPodFilter, ShipmentHasUitCodeFilter,
// CompanyHasTransportContract, CompanyHasWarehousingContract, etc.). The agent's
// plugin descriptions occasionally surface them as true/false, and LLMs sometimes
// pick up that convention. Normalize defensively so a stray "true" / "false" on
// any `has_*` key still lands the user on a correctly-filtered listing.
//
// Other boolean-shaped filters (`invoice`, `admin_enabled`, `oneclick_upload`,
// etc.) genuinely use 'true' / 'false' on both the schema and the listing view —
// they must NOT be normalized.
const normalizeFilterValue = (agentKey: string, value: string): string => {
    if (!agentKey.startsWith('has_')) return value;
    const lower = value.toLowerCase();
    if (lower === 'true')  return 'yes';
    if (lower === 'false') return 'no';
    return value;
};

const buildQuery = (config: NavConfig, filters: Record<string, string>): Record<string, QueryValue> => {
    const query: Record<string, QueryValue> = {};

    for (const [agentKey, entry] of Object.entries(config.filterMapping)) {
        const rawValue = filters[agentKey];
        if (!rawValue) continue;

        const value = normalizeFilterValue(agentKey, rawValue);

        const range = parseDateRangeForUrl(value);
        if (range) {
            query[entry.urlKey]            = 'custom-range';
            query[entry.urlKey + '_range'] = range;
        } else if (value.includes(InterfaceFilterMultipleSeparator.SEPARATOR)) {
            query[entry.urlKey] = value.split(InterfaceFilterMultipleSeparator.SEPARATOR).map(part => normalizeFilterValue(agentKey, part));
        } else {
            query[entry.urlKey] = value;
        }

        if (entry.implies) {
            for (const [impliedKey, impliedVal] of Object.entries(entry.implies)) {
                if (!(impliedKey in query)) {
                    query[impliedKey] = impliedVal;
                }
            }
        }
    }

    return query;
};

const buildParams = (config: NavConfig, filters: Record<string, string>): Record<string, string> => {
    const params: Record<string, string> = { ...(config.staticParams ?? {}) };

    for (const [agentKey, paramName] of Object.entries(config.paramMapping ?? {})) {
        const value = filters[agentKey];
        if (value && !(paramName in params)) {
            params[paramName] = value;
        }
    }

    return params;
};

// Resolves one orchestrator-declared offer into a concrete NavigationAction.
// The label normally comes from the offer (LLM-written, in the user's language).
// If the LLM forgets to provide a label, fall back to NAVIGATION_MAP's static
// label for that route — better a generic button than no button at all.
const mapOfferToAction = (offer: NavigationOffer | undefined | null): NavigationAction | null => {
    if (!offer || !offer.resource) return null;

    const mapping = JSON_API_TO_RESOURCE_TYPE[offer.resource];
    if (!mapping) return null;

    const filters = offer.filters ?? {};
    let route = mapping.listing;
    if (mapping.detail && mapping.detailKey && filters[mapping.detailKey]) {
        route = mapping.detail;
    }

    const config = NAVIGATION_MAP[route];
    if (!config) return null;

    const label = (offer.label && offer.label.trim()) || config.label;
    return { type: 'navigate', label, route, filters };
};

export const mapOffersToActions = (offers: NavigationOffer[] | undefined | null): NavigationAction[] => {
    if (!offers || offers.length === 0) return [];
    const actions: NavigationAction[] = [];
    for (const offer of offers) {
        const action = mapOfferToAction(offer);
        if (action) actions.push(action);
    }
    return actions;
};

const detectConversationLanguage = (text: string | undefined | null): 'ro' | 'en' => {
    if (!text) return 'en';
    if (/[ăâîșțĂÂÎȘȚ]/.test(text)) return 'ro';
    const roWords = /\b(și|si|sau|este|sunt|care|pentru|ce|cum|câte|cate|cât|cat|vreau|arată|arata|transporturi|facturi|comenzi|companie|companii|curse|livrare|unde|când|cand|mulțumesc|multumesc)\b/i;
    return roWords.test(text) ? 'ro' : 'en';
};

export const getNavigationHeading = (text: string | undefined | null): string => {
    return detectConversationLanguage(text) === 'ro'
        ? 'Vrei să te duc la una dintre aceste pagini?'
        : 'Would you like me to take you to one of these pages?';
};

export const useNavigationActions = () => {
    const router    = useRouter();
    const authStore = useAuthStore();
    const cleoStore = useCleoStore();

    const getConfig = (action: NavigationAction): NavConfig | undefined => {
        return NAVIGATION_MAP[action.route];
    };

    const hasAccess = (action: NavigationAction): boolean => {
        const config = getConfig(action);
        if (!config) return false;
        return authStore.accountPermissions.allowedAll(config.requiredKPN);
    };

    const isOnTargetRoute = (action: NavigationAction): boolean => {
        const config = getConfig(action);
        if (!config) return false;
        return router.currentRoute.value.name === config.routeName;
    };

    const navigate = async (action: NavigationAction): Promise<void> => {
        const config = getConfig(action);
        if (!config) return;
        const sameRoute = isOnTargetRoute(action);
        await router.push({
            name:   config.routeName,
            params: buildParams(config, action.filters),
            query:  buildQuery(config, action.filters),
        });
        if (sameRoute) {
            cleoStore.bumpViewReloadKey();
        }
    };

    const getHref = (action: NavigationAction): string | null => {
        const config = getConfig(action);
        if (!config) return null;
        return router.resolve({
            name:   config.routeName,
            params: buildParams(config, action.filters),
            query:  buildQuery(config, action.filters),
        }).href;
    };

    return { hasAccess, navigate, getHref };
};
